import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { Container } from "@/components/site/Container";
import { PageHero } from "@/components/site/PageHero";
import { BlogPostCard } from "@/components/blog/BlogPostCard";
import { BlogSidebar } from "@/components/blog/BlogSidebar";
import { listPublishedPosts } from "@/lib/blog.functions";
import { canonical } from "@/lib/seo";

const searchSchema = z.object({ page: z.number().int().min(1).optional().catch(1) });

export const Route = createFileRoute("/blog/")({
  validateSearch: searchSchema,
  component: BlogIndex,
  head: () => ({
    meta: [
      { title: "Phoenix Roofing Tips & Cost Guides | Durafoam Blog" },
      {
        name: "description",
        content:
          "Expert roofing advice from Phoenix's most trusted foam roofing company. Foam roof tips, cost guides, monsoon prep, and local roofing news since 1989.",
      },
      { property: "og:title", content: "Durafoam Roofing Blog" },
      {
        property: "og:description",
        content:
          "Expert roofing advice from Phoenix's most trusted foam roofing company since 1989.",
      },
      { property: "og:url", content: canonical("/blog") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: canonical("/blog") }],
  }),
});

function BlogIndex() {
  const { page = 1 } = useSearch({ from: "/blog/" });
  const listFn = useServerFn(listPublishedPosts);
  const { data } = useSuspenseQuery({
    queryKey: ["blog", "list", page],
    queryFn: () => listFn({ data: { page, pageSize: 12 } }),
  });

  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));

  return (
    <>
      <PageHero
        pageSlug="blog"
        compact
        eyebrow="The Durafoam Blog"
        h1={<>Phoenix roofing tips, cost guides &amp; expert advice</>}
        subhead="Honest answers from a family-owned foam roofing company that's served the Valley since 1989."
      />


      <section className="bg-[var(--surface)] py-10 lg:py-14">
        <Container className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <div>
            {data.posts.length === 0 ? (
              <p className="text-[var(--muted-foreground)]">No posts yet.</p>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {data.posts.map((p) => (
                  <BlogPostCard key={p.id} post={p} />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <nav className="mt-10 flex items-center justify-center gap-2">
                {page > 1 && (
                  <Link
                    to="/blog"
                    search={{ page: page - 1 }}
                    className="rounded-md border border-border bg-white px-4 py-2 text-sm font-medium hover:bg-[var(--surface)]"
                  >
                    ← Previous
                  </Link>
                )}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <Link
                    key={n}
                    to="/blog"
                    search={{ page: n }}
                    className={`rounded-md px-3 py-2 text-sm font-medium ${
                      n === page
                        ? "bg-primary text-white"
                        : "border border-border bg-white hover:bg-[var(--surface)]"
                    }`}
                  >
                    {n}
                  </Link>
                ))}
                {page < totalPages && (
                  <Link
                    to="/blog"
                    search={{ page: page + 1 }}
                    className="rounded-md border border-border bg-white px-4 py-2 text-sm font-medium hover:bg-[var(--surface)]"
                  >
                    Next →
                  </Link>
                )}
              </nav>
            )}
          </div>

          <BlogSidebar />
        </Container>
      </section>
    </>
  );
}
